import { t } from '../../services/i18n';
import './rc-profile-tab-invoices';
import './rc-profile-tab-profile';
import './rc-profile-tab-subscription';
import { type ProfileViewState } from './profile-shared';
import { RcProfileTabInvoices } from './rc-profile-tab-invoices';
import { RcProfileTabProfile } from './rc-profile-tab-profile';
import { RcProfileTabSubscription } from './rc-profile-tab-subscription';

export class RcProfileView extends HTMLElement {
  state: ProfileViewState | null = null;

  render(): void {
    if (!this.state) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="page-header">
        <h1>${t('pages.profile.title')}</h1>
      </div>
      ${this.renderTabs()}
      <div class="profile-content">
        ${this.renderTabHost()}
      </div>
    `;

    this.querySelectorAll<HTMLButtonElement>('.profile-tab').forEach((button) => {
      button.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('profile-tab-change', {
          bubbles: true,
          detail: { tab: button.dataset.tab },
        }));
      });
    });

    this.renderChildTab();
  }

  private renderTabs(): string {
    if (!this.state) {
      return '';
    }

    const tabs = [
      { key: 'profile', label: t('pages.profile.title') },
      { key: 'subscription', label: t('billing.subscriptionTab') },
      { key: 'invoices', label: t('billing.invoicesTab') },
    ].filter((tab) => this.state?.allowedTabs.includes(tab.key as 'profile' | 'subscription' | 'invoices'));

    return `
      <nav class="profile-tabs">
        ${tabs.map((tab) => `
          <button class="profile-tab${this.state?.activeTab === tab.key ? ' active' : ''}" data-tab="${tab.key}">${tab.label}</button>
        `).join('')}
      </nav>
    `;
  }

  private renderTabHost(): string {
    switch (this.state?.activeTab) {
      case 'profile':
        return '<rc-profile-tab-profile></rc-profile-tab-profile>';
      case 'subscription':
        return '<rc-profile-tab-subscription></rc-profile-tab-subscription>';
      case 'invoices':
        return '<rc-profile-tab-invoices></rc-profile-tab-invoices>';
      default:
        return '';
    }
  }

  private renderChildTab(): void {
    if (!this.state) {
      return;
    }

    if (this.state.activeTab === 'subscription') {
      const subscription = this.querySelector('rc-profile-tab-subscription') as RcProfileTabSubscription | null;
      if (!subscription) return;
      subscription.billingOverview = this.state.billingOverview;
      subscription.isBillingSyncing = this.state.isBillingSyncing;
      subscription.isLoading = this.state.isLoading;
      subscription.isPortalLoading = this.state.isPortalLoading;
      subscription.checkoutLoadingKey = this.state.checkoutLoadingKey;
      subscription.subscriptionLoadingKey = this.state.subscriptionLoadingKey;
      subscription.isCancellingSubscription = this.state.isCancellingSubscription;
      subscription.billingError = this.state.billingError;
      subscription.billingTermsError = this.state.billingTermsError;
      subscription.billingTermsAccepted = this.state.billingTermsAccepted;
      subscription.isAdmin = this.state.isAdmin;
      subscription.canManageAccount = this.state.canManageAccount;
      subscription.render();
      return;
    }

    if (this.state.activeTab === 'profile') {
      const profile = this.querySelector('rc-profile-tab-profile') as RcProfileTabProfile | null;
      if (!profile) return;
      profile.profile = this.state.profile;
      profile.accountMembers = this.state.accountMembers;
      profile.userName = this.state.userName;
      profile.userEmail = this.state.userEmail;
      profile.providerLabel = this.state.providerLabel;
      profile.error = this.state.error;
      profile.isLoading = this.state.isLoading;
      profile.isSaving = this.state.isSaving;
      profile.isSwitchingAccountMode = this.state.isSwitchingAccountMode;
      profile.accountModeError = this.state.accountModeError;
      profile.isManagingAccountMembers = this.state.isManagingAccountMembers;
      profile.accountMembersError = this.state.accountMembersError;
      profile.render();
      return;
    }

    const invoices = this.querySelector('rc-profile-tab-invoices') as RcProfileTabInvoices | null;
    if (!invoices) return;
    invoices.billingOverview = this.state.billingOverview;
    invoices.isLoading = this.state.isLoading;
    invoices.billingError = this.state.billingError;
    invoices.render();
  }
}

if (!customElements.get('rc-profile-view')) {
  customElements.define('rc-profile-view', RcProfileView);
}
